import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox, Loader2, Send, Mail, Phone, CornerDownRight, CheckCircle2, MessagesSquare, Zap, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const STATUS_STYLES = {
  new: "bg-[#D98E73]/15 text-[#B5794E]",
  replied: "bg-[#5C8A72]/15 text-[#5C8A72]",
  closed: "bg-[#8FA89B]/15 text-[#4A5D6B]",
};

function AutoReplyCard({ basePath }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const { data } = useQuery({
    queryKey: ["inquiry-settings", basePath],
    queryFn: () => api.get(`${basePath}/inquiry-settings`).then((r) => r.data),
  });
  if (data && !loaded) { setEnabled(!!data.enabled); setMessage(data.message || ""); setLoaded(true); }

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${basePath}/inquiry-settings`, { enabled, message });
      toast.success("Auto-reply saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="eyw-card p-4 mb-5" data-testid="autoreply-card">
      <button onClick={() => setOpen((v) => !v)} data-testid="autoreply-toggle-open" className="w-full flex items-center justify-between text-left">
        <span className="text-sm font-semibold text-[#1A242B] flex items-center gap-2"><Zap size={15} className="text-[#D98E73]" /> Instant auto-reply {enabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">On</span>}</span>
        <span className="text-xs text-[#144F75]">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-[#4A5D6B]">Every new inquirer gets this reply within seconds (by email, and in their chat if they're signed in). You'll still reply personally.</p>
          <label className="flex items-center gap-2 text-sm text-[#1A242B]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="autoreply-enabled" />
            Send an automatic reply
          </label>
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="autoreply-message" className="eyw-input w-full resize-none text-sm" placeholder="Your friendly auto-reply message…" />
          <button onClick={save} disabled={saving} data-testid="autoreply-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save auto-reply
          </button>
        </div>
      )}
    </div>
  );
}

function InquiryCard({ inq, basePath }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["inquiries", basePath] });

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`${basePath}/inquiries/${inq.id}/reply`, { body: reply });
      toast.success(inq.thread_active ? "Reply sent to their Messages" : "Reply sent by email");
      setReply(""); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const setStatus = async (status) => {
    try { await api.put(`${basePath}/inquiries/${inq.id}/status?status=${status}`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="eyw-card p-5" data-testid={`inquiry-${inq.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-[#1A242B]">{inq.sender_name}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[#4A5D6B] mt-0.5">
            <a href={`mailto:${inq.sender_email}`} className="inline-flex items-center gap-1 hover:text-[#144F75]"><Mail size={12} /> {inq.sender_email}</a>
            {inq.sender_phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {inq.sender_phone}</span>}
            <span>· {timeAgo(inq.created_at)}</span>
          </div>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize shrink-0 ${STATUS_STYLES[inq.status] || STATUS_STYLES.new}`} data-testid={`inquiry-status-${inq.id}`}>{inq.status}</span>
      </div>
      {inq.subject && <p className="text-sm font-semibold text-[#1A242B] mt-3">{inq.subject}</p>}
      <p className="text-sm text-[#4A5D6B] mt-1 whitespace-pre-wrap">{inq.message}</p>

      {(inq.replies || []).length > 0 && (
        <div className="mt-3 space-y-2 border-l-2 border-[#144F75]/20 pl-3">
          {inq.replies.map((r, i) => (
            <div key={i} className="text-sm" data-testid={`inquiry-reply-${inq.id}-${i}`}>
              <span className="inline-flex items-center gap-1 text-[11px] text-[#8FA89B]">
                <CornerDownRight size={11} /> {r.author_name} · {timeAgo(r.created_at)}
                {r.auto && <span className="ml-1 inline-flex items-center gap-0.5 text-[#D98E73]"><Zap size={9} /> auto</span>}
              </span>
              <p className="text-[#1A242B] whitespace-pre-wrap">{r.body}</p>
            </div>
          ))}
        </div>
      )}

      {inq.thread_active && inq.sender_user_id && (
        <Link to={`/messages?to=${inq.sender_user_id}`} data-testid={`inquiry-open-messages-${inq.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#144F75] hover:underline">
          <MessagesSquare size={13} /> Open in Messages
        </Link>
      )}

      {inq.status !== "closed" && (
        <div className="mt-3 flex items-end gap-2">
          <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`inquiry-reply-input-${inq.id}`} className="eyw-input flex-1 resize-none text-sm" placeholder={inq.thread_active ? "Reply (also sent to their Messages)…" : "Write a reply (sent by email)…"} />
          <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid={`inquiry-reply-send-${inq.id}`} className="eyw-btn !px-4 disabled:opacity-50">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />}
          </button>
        </div>
      )}
      <div className="mt-2 flex gap-3 text-xs">
        {inq.status !== "closed"
          ? <button onClick={() => setStatus("closed")} data-testid={`inquiry-close-${inq.id}`} className="text-[#4A5D6B] hover:text-[#144F75] inline-flex items-center gap-1"><CheckCircle2 size={12} /> Mark closed</button>
          : <button onClick={() => setStatus("replied")} data-testid={`inquiry-reopen-${inq.id}`} className="text-[#144F75] hover:underline">Reopen</button>}
      </div>
    </div>
  );
}

export default function InquiriesPanel({ basePath, title = "Inquiries", subtitle }) {
  const [filter, setFilter] = useState("");
  const { data: inquiries = [], isLoading } = useQuery({
    queryKey: ["inquiries", basePath, filter],
    queryFn: () => api.get(`${basePath}/inquiries${filter ? `?status=${filter}` : ""}`).then((r) => r.data),
    refetchInterval: 30000,
  });

  return (
    <div data-testid="inquiries-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Inbox size={22} className="text-[#144F75]" /> {title}</h2>
          <p className="text-sm text-[#4A5D6B]">{subtitle || "Messages from visitors and prospective members — reply here and they'll get it by email."}</p>
        </div>
        <div className="flex gap-1.5">
          {["", "new", "replied", "closed"].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`inquiries-filter-${s || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs capitalize transition-colors ${filter === s ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>
              {s || "all"}
            </button>
          ))}
        </div>
      </div>
      <AutoReplyCard basePath={basePath} />
      {isLoading ? (
        <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
      ) : inquiries.length === 0 ? (
        <p className="text-sm text-[#8FA89B]" data-testid="inquiries-empty">No inquiries yet. When someone reaches out from your public page, it lands here.</p>
      ) : (
        <div className="grid gap-3">
          {inquiries.map((inq) => <InquiryCard key={inq.id} inq={inq} basePath={basePath} />)}
        </div>
      )}
    </div>
  );
}
